import React, { useEffect, useRef, useState } from 'react';
import EditorJS, { OutputData } from '@editorjs/editorjs';
import { useStore } from '../../stores/root-store-context';

// Editor.js Tools
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import Quote from '@editorjs/quote';
import Delimiter from '@editorjs/delimiter';
import Code from '@editorjs/code';
import InlineCode from '@editorjs/inline-code';
import Warning from '@editorjs/warning';
import ImageTool from '@editorjs/image';
import Checklist from '@editorjs/checklist';


// Helper to convert a File to base64
async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result.toString());
      } else {
        reject(new Error('File could not be read'));
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

type RichTextEditorProps = {
  initialData?: OutputData;   // Editor.js type for saved data
  onChange: (data: OutputData) => void;
};

// Enhanced tooltip component
const Tooltip: React.FC<{show: boolean, text: string}> = ({show, text}) => {
  if (!show) return null;
  
  return (
    <div style={{
      position: 'absolute',
      bottom: '-28px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#333',
      color: 'white',
      padding: '3px 6px',
      borderRadius: '3px',
      fontSize: '11px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: 0.95,
    }}>
      {text}
      <div style={{
        position: 'absolute',
        top: '-4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '4px solid transparent',
        borderRight: '4px solid transparent',
        borderBottom: '4px solid #333',
      }} />
    </div>
  );
};

// Enhanced toolbar button component
const ToolbarButton: React.FC<{icon: string, title: string, onClick: () => void, active?: boolean}> = ({ 
  icon, 
  title, 
  onClick, 
  active = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  
  const handleMouseEnter = () => {
    setIsHovered(true);
    tooltipTimer.current = setTimeout(() => {
      setShowTooltip(true);
    }, 400); 
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
    if (tooltipTimer.current) {
      clearTimeout(tooltipTimer.current);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          padding: '0',
          border: 'none',
          borderRadius: '4px',
          backgroundColor: active 
            ? '#e6f7ff' 
            : isHovered 
              ? '#e9ecef' 
              : 'transparent',
          color: active 
            ? '#1890ff' 
            : isHovered 
              ? '#1890ff' 
              : '#495057',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        <span 
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          dangerouslySetInnerHTML={{ __html: icon }} 
        />
      </button>
      <Tooltip show={showTooltip} text={title} />
    </div>
  );
};

// Separator line between toolbar button groups
const ToolbarSeparator = () => (
  <div style={{
    width: '1px',
    height: '18px',
    backgroundColor: '#dee2e6',
    margin: '0 6px',
    alignSelf: 'center',
  }} />
);

// Group of toolbar buttons
const ToolbarGroup: React.FC<{children: React.ReactNode}> = ({ children }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    marginRight: '4px', 
  }}>
    {children}
  </div>
);

// Main RichTextEditor component
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialData,
  onChange,
}) => {
  const { employeesStore, applicationStore } = useStore();
  const editorRef = useRef<HTMLDivElement>(null);
  const editorInstance = useRef<EditorJS | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [activeBlock, setActiveBlock] = useState<string>('paragraph');
  
  // Mention popover state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionPos, setMentionPos] = useState<{ top: number; left: number } | null>(null);

  // Auto-fetch users when editor mounts
  useEffect(() => {
    if (!employeesStore.users || employeesStore.users.length === 0) {
      employeesStore.getUsers();
    }
  }, [employeesStore]);

  const users = employeesStore.users || applicationStore.users || [];

  // Enhanced icons with better SVGs
  const icons = {
    paragraph: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M3 12h18M3 18h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    header: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12V5M4 12V19M4 12H20M20 5V12M20 12V19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    unorderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    orderedList: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 16h2M4 10h2v2H4v2h2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    checklist: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 6h11M9 12h11M9 18h11M5 6l-2 2 2 2M5 12l-2 2 2 2M5 18l-2 2 2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    image: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z M8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    quote: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 21c3.33-3.33 5-6 5-8 0-2-.67-3-2-3-.88 0-1.6.67-1.6 1.5 0 .83.72 1.5 1.6 1.5m8 8c3.33-3.33 5-6 5-8 0-2-.67-3-2-3-.88 0-1.6.67-1.6 1.5 0 .83.72 1.5 1.6 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    code: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    table: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    warning: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8v4m0 4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    delimiter: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    inlineCode: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 16l-6-6 6-6M14 8l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    mention: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorInstance.current) return;

    const initEditor = async () => {
      try {
        if (!editorRef.current) return;
        
        editorInstance.current = new EditorJS({
          holder: editorRef.current,
          data: initialData || { blocks: [] },
          placeholder: 'Start typing here... (type @ to mention a colleague)',
          onReady: () => {
            setIsEditorReady(true);
          },
          async onChange() {
            if (!editorInstance.current) return;
            try {
              const savedData = await editorInstance.current.save();
              onChange(savedData);
            } catch (error) {
              console.error('Error saving Editor.js data:', error);
            }
          },

          tools: {
            header: { class: Header as any, inlineToolbar: true },
            paragraph: { class: Paragraph as any, inlineToolbar: true },
            list: { class: List as any, inlineToolbar: true },
            checklist: { class: Checklist as any, inlineToolbar: true }, 
            orderedList: { 
              class: List as any, 
              inlineToolbar: true,
              config: {
                defaultStyle: 'ordered'
              }
            },
            embed: { class: Embed as any, inlineToolbar: true },
            table: { class: Table as any, inlineToolbar: true },
            quote: { class: Quote as any, inlineToolbar: true },
            delimiter: Delimiter as any,
            code: { class: Code as any },
            inlineCode: { class: InlineCode as any },
            warning: { class: Warning as any, inlineToolbar: true },
            image: {
              class: ImageTool as any,
              inlineToolbar: true,
              config: {
                uploader: {
                  async uploadByFile(file: File) {
                    try {
                      const base64 = await readFileAsBase64(file);
                      return { success: 1, file: { url: base64 } };
                    } catch (err) {
                      return { success: 0, message: 'Upload failed' };
                    }
                  },
                  async uploadByUrl(url: string) {
                    try {
                      const response = await fetch(url);
                      const blob = await response.blob();
                      const file = new File([blob], 'image', { type: blob.type });
                      const base64 = await readFileAsBase64(file);
                      return { success: 1, file: { url: base64 } };
                    } catch (err) {
                      return { success: 0, message: 'Upload failed' };
                    }
                  },
                },
              },
            },
          },
        });

        editorInstance.current.on('block-changed', () => {
          updateToolbarState();
        });

      } catch (error) {
        console.error('Error initializing editor:', error);
      }
    };

    initEditor();

    return () => {
      try {
        editorInstance.current?.destroy();
        editorInstance.current = null;
      } catch (error) {
        console.error('Error destroying editor:', error);
      }
    };
  }, []);

  // Listen for @ keyup inside the EditorJS contenteditable container
  const handleEditorKeyUp = () => {
    const sel = window.getSelection();
    if (!sel || !sel.focusNode) {
      setMentionQuery(null);
      return;
    }

    const text = sel.focusNode.textContent || '';
    const offset = sel.focusOffset;
    const textBeforeCursor = text.slice(0, offset);

    const match = textBeforeCursor.match(/@([a-zA-Zа-яА-Я0-9_]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      try {
        const range = sel.getRangeAt(0).cloneRange();
        const rect = range.getBoundingClientRect();
        const parentRect = editorRef.current?.getBoundingClientRect();

        if (parentRect) {
          setMentionPos({
            top: rect.bottom - parentRect.top + 24,
            left: Math.max(10, rect.left - parentRect.left),
          });
        }
      } catch {
        setMentionPos({ top: 40, left: 10 });
      }
    } else {
      setMentionQuery(null);
    }
  };

  // Select employee from inline @ popover (Teams-style mention tag insertion)
  const selectEmployee = (u: any) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;
    const userName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;

    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent || '';
      const offset = range.startOffset;
      const match = text.slice(0, offset).match(/@([a-zA-Zа-яА-Я0-9_]*)$/);

      if (match) {
        const startIdx = offset - match[0].length;
        const beforeText = text.slice(0, startIdx);
        const afterText = text.slice(offset);

        textNode.textContent = beforeText;

        // Create Teams-style styled mention tag element (contenteditable=false ensures next typing is normal text)
        const mentionEl = document.createElement('b');
        mentionEl.className = 'mention-pill';
        mentionEl.setAttribute('data-user-id', String(u.id));
        mentionEl.setAttribute('contenteditable', 'false');
        mentionEl.style.color = '#6264a7';
        mentionEl.style.backgroundColor = '#f3f2f1';
        mentionEl.style.padding = '2px 6px';
        mentionEl.style.borderRadius = '4px';
        mentionEl.style.fontWeight = '600';
        mentionEl.style.display = 'inline-block';
        mentionEl.style.margin = '0 2px';
        mentionEl.style.userSelect = 'none';
        mentionEl.textContent = `@${userName}`;

        const spaceNode = document.createTextNode('\u00A0');
        const afterNode = document.createTextNode(afterText);

        const parent = textNode.parentNode;
        if (parent) {
          parent.insertBefore(mentionEl, textNode.nextSibling);
          parent.insertBefore(spaceNode, mentionEl.nextSibling);
          parent.insertBefore(afterNode, spaceNode.nextSibling);

          // Position cursor in normal text node after the space
          try {
            const newRange = document.createRange();
            newRange.setStart(afterNode, 0);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          } catch (e) {
            console.log('Range set error:', e);
          }
        }
      }
    }

    if (editorInstance.current) {
      editorInstance.current.save().then((saved) => onChange(saved));
    }

    setMentionQuery(null);
  };

  // Filter matching users
  const filteredUsers = mentionQuery === null
    ? []
    : users.filter((u) => {
        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const q = mentionQuery.toLowerCase();
        return fullName.includes(q) || email.includes(q);
      });

  const updateToolbarState = () => {
    if (!editorInstance.current) return;
    try {
      const currentBlockIndex = editorInstance.current.blocks.getCurrentBlockIndex();
      const currentBlock = editorInstance.current.blocks.getBlockByIndex(currentBlockIndex);
      if (currentBlock) {
        setActiveBlock(currentBlock.name);
      }
    } catch (error) {
      console.error('Error updating toolbar state:', error);
    }
  };

  const insertBlock = async (type: string) => {
    if (!editorInstance.current) return;
    await editorInstance.current.blocks.insert(type);
    setActiveBlock(type);
  };

  // Insert @ character into current block from toolbar
  const insertMentionChar = async () => {
    const sel = window.getSelection();
    if (sel && sel.focusNode) {
      const range = sel.getRangeAt(0);
      const textNode = document.createTextNode('@');
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      handleEditorKeyUp();
    }
  };

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // Reset selectedIndex whenever query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionQuery]);

  // Keep refs updated for capture-phase listener
  const mentionQueryRef = useRef(mentionQuery);
  const filteredUsersRef = useRef(filteredUsers);
  const selectedIndexRef = useRef(selectedIndex);
  const selectEmployeeRef = useRef(selectEmployee);

  useEffect(() => {
    mentionQueryRef.current = mentionQuery;
    filteredUsersRef.current = filteredUsers;
    selectedIndexRef.current = selectedIndex;
    selectEmployeeRef.current = selectEmployee;
  });

  // Native capture-phase keydown listener to intercept Enter/Tab before EditorJS splits line
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    const handleKeyDownCapture = (e: KeyboardEvent) => {
      if (mentionQueryRef.current !== null && filteredUsersRef.current.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setSelectedIndex((prev) => (prev + 1) % filteredUsersRef.current.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setSelectedIndex((prev) => (prev - 1 + filteredUsersRef.current.length) % filteredUsersRef.current.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const targetUser = filteredUsersRef.current[selectedIndexRef.current];
          if (targetUser) {
            selectEmployeeRef.current(targetUser);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setMentionQuery(null);
          return;
        }
      }
    };

    el.addEventListener('keydown', handleKeyDownCapture, true);
    return () => {
      el.removeEventListener('keydown', handleKeyDownCapture, true);
    };
  }, []);

  return (
    <div
      className="rich-text-editor"
      style={{
        borderRadius: 6,
        overflow: 'visible',
        border: '1px solid #dce0e5',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        position: 'relative',
      }}
    >
      {/* Custom Toolbar */}
      {(isEditorReady || editorInstance.current) && (
        <div
          className="editor-toolbar"
          style={{
            display: 'flex',
            padding: '6px 8px',
            borderBottom: '1px solid #dce0e5',
            backgroundColor: '#f5f7fa',
            borderRadius: '5px 5px 0 0',
            flexWrap: 'wrap',
            position: 'relative',
          }}
        >
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.paragraph}
              title="Paragraph"
              onClick={() => insertBlock('paragraph')}
              active={activeBlock === 'paragraph'}
            />
            <ToolbarButton
              icon={icons.header}
              title="Heading"
              onClick={() => insertBlock('header')}
              active={activeBlock === 'header'}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.unorderedList}
              title="Bullet List"
              onClick={() => insertBlock('list')}
              active={activeBlock === 'list'}
            />
            <ToolbarButton
              icon={icons.orderedList}
              title="Numbered List"
              onClick={() => insertBlock('orderedList')}
              active={activeBlock === 'orderedList'}
            />
            <ToolbarButton
              icon={icons.checklist}
              title="Checklist"
              onClick={() => insertBlock('checklist')}
              active={activeBlock === 'checklist'}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.image}
              title="Image"
              onClick={() => insertBlock('image')}
              active={activeBlock === 'image'}
            />
            <ToolbarButton
              icon={icons.quote}
              title="Quote"
              onClick={() => insertBlock('quote')}
              active={activeBlock === 'quote'}
            />
            <ToolbarButton
              icon={icons.code}
              title="Code Block"
              onClick={() => insertBlock('code')}
              active={activeBlock === 'code'}
            />
            <ToolbarButton
              icon={icons.inlineCode}
              title="Inline Code"
              onClick={() => insertBlock('inlineCode')}
              active={activeBlock === 'inlineCode'}
            />
            <ToolbarButton
              icon={icons.mention}
              title="Mention User (@)"
              onClick={insertMentionChar}
            />
          </ToolbarGroup>
          
          <ToolbarSeparator />
          
          <ToolbarGroup>
            <ToolbarButton
              icon={icons.table}
              title="Table"
              onClick={() => insertBlock('table')}
              active={activeBlock === 'table'}
            />
            <ToolbarButton
              icon={icons.warning}
              title="Warning"
              onClick={() => insertBlock('warning')}
              active={activeBlock === 'warning'}
            />
            <ToolbarButton
              icon={icons.delimiter}
              title="Delimiter"
              onClick={() => insertBlock('delimiter')}
              active={activeBlock === 'delimiter'}
            />
          </ToolbarGroup>
        </div>
      )}
      
      {/* Editor Container */}
      <div
        style={{
          padding: '16px 12px',
          minHeight: 180,
          position: 'relative',
        }}
        ref={editorRef}
        onKeyUp={handleEditorKeyUp}
      />

      {/* Floating @ Mention Popover directly inside EditorJS */}
      {mentionQuery !== null && mentionPos && (
        <div
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            top: mentionPos.top,
            left: mentionPos.left,
            zIndex: 9999,
            backgroundColor: '#ffffff',
            borderRadius: 6,
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            border: '1px solid #d9d9d9',
            maxHeight: 200,
            overflowY: 'auto',
            minWidth: 220,
            padding: '4px 0',
          }}
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((u, index) => {
              const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={u.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectEmployee(u);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background-color 0.15s ease',
                    backgroundColor: isSelected ? '#e6f7ff' : '#ffffff',
                  }}
                >
                  <span style={{ fontWeight: isSelected ? 700 : 600, color: '#1890ff' }}>@{fullName}</span>
                  {u.email && <span style={{ color: '#8c8c8c', fontSize: 11 }}>{u.email}</span>}
                </div>
              );
            })
          ) : (
            <div style={{ padding: '8px 12px', color: '#8c8c8c', fontSize: 12 }}>
              Пользователь не найден
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;